/**
 * Minimal, dependency-free JWT payload decoding.
 *
 * We never verify signatures here (the server does that) — we only need to read
 * claims like `jti`, which is part of the MEDIS access-validator material.
 */

interface JwtClaims {
  jti?: string;
  exp?: number;
  [key: string]: unknown;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const withPadding = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  // atob → binary string; decode as UTF-8 to handle non-ASCII claim values.
  const binary = atob(withPadding);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1]!)) as JwtClaims;
  } catch {
    return null;
  }
}

/** The `jti` claim of the given token, or null if absent/unparseable. */
export function getJti(token: string): string | null {
  return decodeJwt(token)?.jti ?? null;
}
